import { useLayoutEffect, useMemo, useRef, useState } from "react"
import "./quiz.less"
import { classNames, hasText, parseArray } from "utils/helpers"
import PresentationImage from "../components/media/presentation-image"
import PresentationQuestion from "../components/media/presentation-question"
import { useMessages } from "play/language/language"
import { sanitizeEditorPreviewHtml } from "utils/html-sanitization"
import { PlayQuizAnswer } from "play/play-model"
import { usePlayState } from "play/hooks/use-play-state"
import { QuizAnswer } from "models/tables"

export default function Quiz() {
  const { screen, quizAnswers, showCorrectAnswers, modifyState } =
    usePlayState()

  function answerSelected(answerId: string) {
    modifyState((state) => {
      if (state.screen === null) {
        console.warn("Screen is null!")
        return
      }
      if (state.finished) return
      if (state.isTeacherUrl) return

      const countRequired = state.quizAnswers.reduce(
        (sum, answer) => (answer.isCorrect ? sum + 1 : sum),
        0
      )

      const answer = state.quizAnswers.find((x) => x.id === answerId)
      if (answer) {
        if (countRequired === 1) {
          // if we one correct answer, toggle all others
          state.quizAnswers.forEach((a) => {
            a.isSelected = a.id === answerId
          })
        } else {
          // if we have more than one correct, toggle just the selected
          answer.isSelected = !answer.isSelected
        }
      } else {
        console.error("Answer not found by id %s!", answerId)
      }

      const countSelected = state.quizAnswers.reduce(
        (sum, answer) => (answer.isSelected ? sum + 1 : sum),
        0
      )

      state.nextScreenButtonIsActive = countSelected >= countRequired
    })
  }

  return (
    <QuizScreenView
      questionText={screen?.questionText}
      questionImage={screen?.questionImage}
      questionImageWidth={screen?.questionImageWidth || 0}
      questionImageHeight={screen?.questionImageHeight || 0}
      quizAnswers={quizAnswers}
      showCorrect={showCorrectAnswers}
      onAnswerSelected={answerSelected}
    />
  )
}

type Props = {
  questionText: string | undefined
  questionImage: string | undefined
  questionImageWidth: number
  questionImageHeight: number
  quizAnswers: PlayQuizAnswer[]
  showCorrect: boolean
  onAnswerSelected(answerId: string): void
}

function QuizAnswerText(props: { answer: QuizAnswer }) {
  const { answer } = props
  const answerText = useMemo(
    () => sanitizeEditorPreviewHtml(answer.text),
    [answer.text]
  )

  if (hasText(answer.text)) {
    return <div dangerouslySetInnerHTML={{ __html: answerText }} />
  } else {
    return null
  }
}

function QuizAnswerImage(props: { answer: QuizAnswer }) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [state, setState] = useState({
    width: 0,
    height: 0,
  })
  const { answer } = props

  function onImageLoaded() {
    if (imgRef.current) {
      const imgWidth = imgRef.current.naturalWidth
      const imgHeight = imgRef.current.naturalHeight

      setState({
        width: imgWidth,
        height: imgHeight,
      })
    }
  }

  if (hasText(answer.url)) {
    return (
      <img
        className={classNames({
          w: state.width >= state.height,
          h: state.height > state.width,
        })}
        ref={imgRef}
        src={answer.url}
        onLoad={onImageLoaded}
      />
    )
  } else {
    return null
  }
}

export function QuizScreenView(props: Props) {
  const topRef = useRef<HTMLDivElement>(null)
  const imgContainerRef = useRef<HTMLDivElement>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const {
    onAnswerSelected,
    quizAnswers,
    showCorrect,
    questionImageWidth,
    questionImageHeight,
    questionText,
    questionImage,
  } = props
  const [forceRedraw, setForceRedraw] = useState(0)
  const msg = useMessages()

  // useLayoutEffect(() => {
  //   alert(`${window.innerWidth}x${window.innerHeight}`)
  // }, [])

  const allAnswersHaveSmallTextLength = useMemo(() => {
    if (screen !== null) {
      const map = quizAnswers.map((x) => {
        const e = document.createElement("div")
        e.innerHTML = x.text

        return {
          txt: e.innerText,
          len: e.innerText.length,
        }
      })

      for (let answer of map) {
        // split by unicode whitespace and punctuation
        // https://unicode.org/reports/tr18/#General_Category_Property
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape
        const words = answer.txt.split(/\p{Z}|\p{P}/u)
        for (let word of words) {
          if (word.length > 8) {
            return false
          }
        }

        if (answer.len > 50) {
          return false
        }
      }
      return true
    } else {
      return true
    }
  }, [quizAnswers])

  function handleToggleZoom() {
    setIsZoomed((prevIsZoomed) => !prevIsZoomed)
  }

  const zoom = questionImageWidth >= questionImageHeight ? "w" : "h"
  let className = zoom
  if (isZoomed) {
    className += " zoomed"
  }
  const alt = msg.quiz_alt

  let quizTopClass = ""
  if (!hasText(questionText)) {
    quizTopClass = "only-image"
  } else if (!hasText(questionImage)) {
    quizTopClass = "only-text"
  }

  const quizLetters = parseArray(msg.quiz_letters)

  const answers = quizAnswers.map((x) => {
    let res = {
      ...x,
      className: "answer",
      isImage: typeof x.url === "string" && x.url.length > 0,
    }
    if (allAnswersHaveSmallTextLength) {
      res.className += " small-text-length"
    } else {
      res.className += " large-text-length"
    }
    if (res.isSelected) {
      res.className += " selected"
    }
    if (showCorrect) {
      if (res.isCorrect) {
        res.className += " correct"
      } else if (res.isSelected) {
        res.className += " incorrect"
      }
    }
    return res
  })

  const hasImageAnswer =
    answers.findIndex((x) => typeof x.url === "string" && x.url.length > 0) !==
    -1

  // force update
  useLayoutEffect(() => {
    setForceRedraw((prev) => prev + 1)
  }, [imgContainerRef.current])

  return (
    <div className={`quiz-screen ${className} play-screen`}>
      <div className={`quiz-top ${quizTopClass}`} ref={topRef}>
        <div
          ref={imgContainerRef}
          style={{ overflow: "hidden", background: "transparent" }}
        >
          {imgContainerRef.current && hasText(questionImage) && (
            <PresentationImage
              className="quiz-image"
              url={questionImage!}
              imageWidth={questionImageWidth}
              imageHeight={questionImageHeight}
              toggleZoom={handleToggleZoom}
              alt={alt}
              isZoomed={isZoomed}
              zoomContainer={imgContainerRef}
            />
          )}
        </div>
        <PresentationQuestion
          className="quiz-question"
          question={questionText}
        />
      </div>
      <div className="quiz-answers-outer">
        <ul
          className={classNames("quiz-answers px-0", {
            "has-images": hasImageAnswer,
          })}
        >
          {answers.map((answer, answerIndex) => (
            <li key={answer.id} className={`${answer.className}`}>
              <div className="letter">{quizLetters[answerIndex]}</div>
              <button
                className={classNames("answer-content", {
                  "is-image": answer.isImage,
                  "is-text": !answer.isImage,
                })}
                disabled={showCorrect}
                onClick={() => onAnswerSelected(answer.id as string)}
              >
                {answer.isImage ? (
                  <QuizAnswerImage answer={answer} />
                ) : (
                  <QuizAnswerText answer={answer} />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
